import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminTable from "../../components/AdminTable";
import * as api from "../../services/api";

export default function AdminRuns() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate("/");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api.getAdminRuns(150)
      .then((data) => setRuns(data.runs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filteredRuns = useMemo(() => {
    return runs.filter((run) => {
      if (statusFilter !== "all" && run.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        String(run.tool_name || "").toLowerCase().includes(q) ||
        String(run.tool_slug || "").toLowerCase().includes(q) ||
        String(run.user_email || "guest").toLowerCase().includes(q)
      );
    });
  }, [runs, search, statusFilter]);

  const columns = [
    { key: "tool_name", label: "Tool" },
    {
      key: "user_email",
      label: "User",
      render: (v: string) => v || "Guest",
    },
    {
      key: "status",
      label: "Status",
      render: (v: string) => (
        <span
          className={
            "px-2 py-0.5 rounded-full text-xs font-semibold " +
            (v === "completed"
              ? "bg-green-100 text-green-800"
              : v === "failed"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-700")
          }
        >
          {v}
        </span>
      ),
    },
    {
      key: "duration_ms",
      label: "Duration",
      render: (v: number) => (v ? v + "ms" : "-"),
    },
    {
      key: "created_at",
      label: "Created",
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ];

  if (loading || authLoading) return <div className="p-12">Loading...</div>;
  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Recent Tool Runs</h1>
        <p className="text-gray-600">Operational visibility into platform execution activity.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
        <input
          type="text"
          placeholder="Search by tool or user email"
          className="w-full md:w-80 px-4 py-2 border rounded-lg"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-3 py-2 border rounded-lg"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <AdminTable columns={columns} data={filteredRuns} />
    </div>
  );
}
